import { Link } from 'react-router';
import type { Page } from '@/api/types';
import { useSession } from '@/stores/session';
import { Icon } from '@/components/Icon';
import { ErrorState, Skeleton } from '@/components/States';
import { legacyHref } from '@/shell/nav';
import { dateTime, money, num, qty } from '@/lib/format';
import { Overlay } from './ListKit';
import { Avail } from './StockCards';
import { useResource } from './api';
import type { StockSummaryRow, WarehouseStock } from './types';
import s from './ProductStockSheet.module.css';

/**
 * 产品库存：点库存卡 / 表格行打开。手机底部 sheet，iPad / 桌面右侧抽屉。
 * 汇总行来自 /api/inventory/summary（跨仓求和），分仓数字另取 /api/inventory/stocks?productId=。
 */
export function ProductStockSheet({ productId, row, sheet, onClose }: { productId: string; row?: StockSummaryRow; sheet: boolean; onClose: () => void }) {
  const can = useSession((st) => st.can);
  const { data, loading, error, reload } = useResource<Page<WarehouseStock>>(`/api/inventory/stocks?productId=${encodeURIComponent(productId)}&pageSize=100`);
  const stocks = data?.items || [];
  const product = stocks[0]?.product;
  const name = row?.productName || product?.name || '产品库存';
  const code = row?.productCode || product?.code || '';
  const cur = row ? row.currentQuantity : stocks.reduce((a, x) => a + num(x.currentQuantity), 0);
  const res = row ? row.reservedQuantity : stocks.reduce((a, x) => a + num(x.reservedQuantity), 0);
  const avail = row ? row.availableQuantity : cur - res;
  const value = row?.inventoryValue;
  const canTx = can('inventory.transactions.read');

  return (
    <Overlay
      sheet={sheet}
      title="产品库存"
      onClose={onClose}
      footer={
        <>
          {canTx && (
            <Link className={s.btn} to={`/inventory/transactions?productId=${encodeURIComponent(productId)}&pname=${encodeURIComponent(code || name)}`}>
              <Icon name="list" />看出入库流水
            </Link>
          )}
          <a className={s.btn} href={legacyHref(`/inventory?productId=${encodeURIComponent(productId)}`)} title="只读体验期：入库、报损、盘点在老版进行">
            <Icon name="return" />在老版中操作
          </a>
        </>
      }
    >
      <div className={s.body}>
        <div className={s.head}>
          <h2>{name}</h2>
          <p><span className="tnum">{code}</span>{(row?.category || product?.category) && <span>{row?.category || product?.category}</span>}</p>
        </div>
        <div className={s.tot}>
          <div><span>现有</span><b className="tnum">{qty(cur)}</b></div>
          <div><span>预占</span><b className="tnum">{qty(res)}</b></div>
          <div><span>可用</span><b className="tnum"><Avail value={avail} /></b></div>
        </div>
        {value !== undefined && <p className={s.value}>库存价值 <b className="tnum">{money(value)}</b></p>}
        {num(avail) < 0 && (
          <p className={s.neg}>可用为负是允许的：订单已预占、货还没到。补货入库后自动转正，不影响继续接单。</p>
        )}

        <h3 className={s.sec}>分仓库存</h3>
        {error ? (
          <ErrorState title="分仓库存加载失败" message={error.message} detail="GET /api/inventory/stocks" onRetry={reload} />
        ) : loading && !stocks.length ? (
          <Skeleton lines={3} />
        ) : stocks.length === 0 ? (
          <p className={s.none}>这个产品还没有在任何仓库入过库。</p>
        ) : (
          <ul className={s.list}>
            {stocks.map((w) => (
              <li key={w.id}>
                <div className={s.wh}>
                  <b>{w.warehouse.name}</b>
                  <span className="tnum em">{w.warehouse.code}</span>
                  <span className={s.upd}>{dateTime(w.updatedAt)} 更新</span>
                </div>
                <dl className={s.kv}>
                  <div><dt>现有</dt><dd className="tnum">{qty(w.currentQuantity)}</dd></div>
                  <div><dt>预占</dt><dd className="tnum">{qty(w.reservedQuantity)}</dd></div>
                  <div><dt>可用</dt><dd className="tnum"><Avail value={w.availableQuantity} strong /></dd></div>
                  {w.inventoryUnitCost !== undefined && <div><dt>入库均价</dt><dd className="tnum">{money(w.inventoryUnitCost)}</dd></div>}
                  {w.inventoryValue !== undefined && <div><dt>价值</dt><dd className="tnum">{money(w.inventoryValue)}</dd></div>}
                </dl>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Overlay>
  );
}
